import React, { Suspense } from 'react';
import Container from '@mui/material/Container';

import { Navigate } from 'react-router';
import SupplyChain from './SupplyChain';
import { determineDashboardEntryPath, determineReportsEntryPath, determineStatusEntryPath, determineTaxonomyEntryPath } from './utils';
import FullPageLoadingIndicator from 'components/v2/FullPageLoadingIndicator';
import { canAccessApprovalCenter } from 'pages/ApprovalCenter/utils/access';
import PublicSupplyChainIntlProvider from 'pages/SupplyChain/PublicSupplyChainIntlProvider';
import { fullPageHeight } from 'utils';

const ActionDetail = React.lazy(() => import('pages/Action/Detail'));
const DocumentHub = React.lazy(() => import('pages/DocumentHub/index'));
const Actions = React.lazy(() => import('pages/Action/index'));
const ActivateUser = React.lazy(() => import('components/Auth/ActivateUser'));
const ApprovalCenter = React.lazy(() => import('pages/ApprovalCenter/index'));
const BusinessActivity = React.lazy(() => import('pages/Taxonomy/BusinessActivity'));
const ClimateRiskActions = React.lazy(() => import('pages/ClimateRisk/Actions'));
const ClimateRiskAnalyses = React.lazy(() => import('pages/ClimateRisk/Analyses'));
const ClimateRiskAnalysis = React.lazy(() => import('pages/ClimateRisk/components/Analyses/Analysis'));
const ClimateRisks = React.lazy(() => import('pages/ClimateRisk/Risks'));
const ClimateRiskSegments = React.lazy(() => import('pages/ClimateRisk/Segments'));
const DMAPage = React.lazy(() => import('pages/DMA/index'));
const Compensation = React.lazy(() => import('pages/Compensation/index'));
const Eudr = React.lazy(() => import('pages/Eudr/index'));
const EudrProductDetail = React.lazy(() => import('pages/Eudr/Products/Detail/index'));
const Ppwr = React.lazy(() => import('pages/Ppwr/index'));
const PpwrPackagingDetail = React.lazy(() => import('pages/Ppwr/Packagings/Detail/index'));
const PpwrRiskDetail = React.lazy(() => import('pages/Ppwr/Risks/RiskDetail'));
const PpwrMeasureDetail = React.lazy(() => import('pages/Ppwr/Measures/MeasureDetail'));
const EmissionDetail = React.lazy(() => import('pages/Emission/EmissionDetail'));
const EmissionFootprints = React.lazy(() => import('pages/Emission/index'));
const EmissionResults = React.lazy(() => import('pages/Emission/EmissionResults'));
const EmissionsViewRef = React.lazy(() => import('pages/StatusDashboard/EmissionsViewRef'));
const Export = React.lazy(() => import('pages/Export/index'));
const GeneralRiskDetails = React.lazy(() => import('pages/RiskModule/GeneralRiskManagement/Risks/RiskDetails'));
const GeneralRiskManagementIndex = React.lazy(() => import('pages/RiskModule/GeneralRiskManagement/index'));
const GeneralRiskMitigationControlDetails = React.lazy(
   () => import('pages/RiskModule/GeneralRiskManagement/RiskMitigationControls/RiskMitigationControlDetails')
);
const GeneralRiskMitigationDetails = React.lazy(() => import('pages/RiskModule/GeneralRiskManagement/RiskMitigations/RiskMitigationDetails'));
const GeneralRiskMitigationTaskDetails = React.lazy(
   () => import('pages/RiskModule/GeneralRiskManagement/RiskMitigationTasks/RiskMitigationTaskDetails')
);
const Home = React.lazy(() => import('pages/Home'));
const Hub = React.lazy(() => import('pages/ClosingHub'));
const ImportDataPage = React.lazy(() => import('pages/DataSource'));
const ImportDefinitionPage = React.lazy(() => import('pages/DataSource/ImportDefinition'));
const ImportJobsPage = React.lazy(() => import('pages/DataSource/ImportJobs'));
const KeyFiguresViewRef = React.lazy(() => import('pages/StatusDashboard/KeyFigureViewRef'));
const KPIDetailPage = React.lazy(() => import('pages/KPI/index'));
const KPIScopeSettingsESRS = React.lazy(() => import('components/KPI/KPIScope/ESRS'));
const KPIScopeSettingsGeneric = React.lazy(() => import('components/KPI/KPIScope/Generic'));
const KPIScopeSettingsGRI = React.lazy(() => import('components/KPI/KPIScope/GRI'));
const Layout = React.lazy(() => import('pages/Layout/index'));
const LiveTemplateDetail = React.lazy(() => import('components/Emissions/LiveTemplates/LiveTemplateDetail'));
const LiveTemplates = React.lazy(() => import('pages/Emission/LiveTemplates'));
const Login = React.lazy(() => import('pages/Login/index'));
const NotAllowed = React.lazy(() => import('pages/NotAllowed/index'));
const NotFound = React.lazy(() => import('pages/NotFound/index'));
const Overview = React.lazy(() => import('pages/Dashboard/index'));
const PublicSupplyChainSurveyRedirect = React.lazy(() => import('pages/SupplyChain/PublicSupplyChainSurveyRedirect'));
const PublicSurveyPage = React.lazy(() => import('pages/PublicSurvey'));
const PublicSurveyOverviewPage = React.lazy(() => import('pages/PublicSurveyOverview'));
const PublicSurveyResponseDetails = React.lazy(() => import('pages/PublicSurveyOverview/PublicSurveyResponseDetails'));
const ProductHub = React.lazy(() => import('pages/ProductHub/index'));
const Profile = React.lazy(() => import('pages/User/index'));
const ReductionPlan = React.lazy(() => import('pages/Emission/ReductionPlan/index'));
const ReductionPlanDetail = React.lazy(() => import('pages/Emission/ReductionPlan/ReductionPlanDetail'));
const ReportEditor = React.lazy(() => import('pages/ReportEditor'));
const ReportEditorPage = React.lazy(() => import('pages/ReportEditor'));
const Reports = React.lazy(() => import('pages/Report/index'));
const ReportSubpages = React.lazy(() => import('pages/Reports'));
const RequireFeatureAccess = React.lazy(() => import('components/Auth/RequireFeatureAccess'));
const RequireAuth = React.lazy(() => import('components/Auth/RequireAuth'));
const RequireNoAuth = React.lazy(() => import('components/Auth/RequireNoAuth'));
const RequireUserContext = React.lazy(() => import('components/v2/RequireOrganisationContext'));
const ResetPasswordNoAuth = React.lazy(() => import('components/Auth/ResetPasswordNoAuth'));
const Settings = React.lazy(() => import('pages/Settings'));
const StrategyDetails = React.lazy(() => import('pages/StrategyModule/Strategy/StrategyDetails'));
const StrategyModuleIndex = React.lazy(() => import('pages/StrategyModule/index'));
const StrategyTargetActionDetails = React.lazy(() => import('pages/StrategyModule/Actions/StrategyTargetActionDetails'));
const StrategyTargetDetails = React.lazy(() => import('pages/StrategyModule/Targets/StrategyTargetDetails'));
const Tasks = React.lazy(() => import('pages/Task/index'));
const TasksDetail = React.lazy(() => import('pages/Task/Detail'));
const Taxonomy = React.lazy(() => import('pages/Taxonomy/index'));
const Validate2FA = React.lazy(() => import('pages/2FA/Validate'));

const TaxonomyRedirect = () => <Navigate replace to={determineTaxonomyEntryPath()} />;
const DocumentHubRedirect = () => <Navigate replace to="/reports/document-hub/overview" />;
const ReportsRedirect = () => <Navigate replace to={determineReportsEntryPath()} />;

export default function generateRoutes() {
   return [
      {
         path: '/public/supplychain/survey/:id',
         element: (
            <PublicSupplyChainIntlProvider>
               <Suspense fallback={<FullPageLoadingIndicator />}>
                  <PublicSurveyPage />
               </Suspense>
            </PublicSupplyChainIntlProvider>
         ),
      },
      {
         path: '/public/supplychain/surveys',
         element: (
            <PublicSupplyChainIntlProvider>
               <Suspense fallback={<FullPageLoadingIndicator />}>
                  <PublicSurveyOverviewPage />
               </Suspense>
            </PublicSupplyChainIntlProvider>
         ),
      },
      {
         path: '/public/supplychain/surveys/responses/:questionnaireResponseId',
         element: (
            <PublicSupplyChainIntlProvider>
               <Suspense fallback={<FullPageLoadingIndicator />}>
                  <PublicSurveyResponseDetails />
               </Suspense>
            </PublicSupplyChainIntlProvider>
         ),
      },
      {
         path: '/login',
         element: (
            <Suspense fallback={<FullPageLoadingIndicator />}>
               <RequireNoAuth>
                  <Login />
               </RequireNoAuth>
            </Suspense>
         ),
      },
      {
         path: '/validate',
         element: (
            <Suspense fallback={<FullPageLoadingIndicator />}>
               <RequireNoAuth>
                  <Validate2FA />
               </RequireNoAuth>
            </Suspense>
         ),
      },
      {
         path: '/resetpassword/:token',
         element: (
            <Suspense fallback={<FullPageLoadingIndicator />}>
               <ResetPasswordNoAuth />
            </Suspense>
         ),
      },
      {
         path: '/activate/:token',
         element: (
            <Suspense fallback={<FullPageLoadingIndicator />}>
               <ActivateUser />
            </Suspense>
         ),
      },
      {
         path: '/supplychain/survey/:id',
         element: (
            <Suspense fallback={<FullPageLoadingIndicator />}>
               <PublicSupplyChainSurveyRedirect />
            </Suspense>
         ),
      },

      {
         path: '/',
         element: (
            <Suspense fallback={<FullPageLoadingIndicator />}>
               <RequireAuth>
                  <RequireUserContext>
                     <Layout />
                  </RequireUserContext>
               </RequireAuth>
            </Suspense>
         ),
         children: [
            {
               path: '/risk-management/general-risk-assessment',
               element: <Navigate replace to="/risk-management/general-risk-assessment/dashboard" />,
            },
            {
               key: 'generalRiskManagement',
               path: '/risk-management/general-risk-assessment/:activeTab',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <GeneralRiskManagementIndex />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'riskDetail',
               path: '/risk-management/general-risk-assessment/risks/detail/:riskId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <GeneralRiskDetails />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'riskMitigationDetail',
               path: '/risk-management/general-risk-assessment/risk-mitigations/detail/:riskMitigationId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <GeneralRiskMitigationDetails />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'riskMitigationTaskDetail',
               path: '/risk-management/general-risk-assessment/risk-mitigation-tasks/detail/:riskMitigationTaskId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <GeneralRiskMitigationTaskDetails />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'riskMitigationControlDetail',
               path: '/risk-management/general-risk-assessment/risk-mitigation-controls/detail/:riskMitigationControlId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <GeneralRiskMitigationControlDetails />
                     </Suspense>
                  </Container>
               ),
            },
            { path: '/strategy-module', element: <Navigate replace to="/strategy-module/dashboard" /> },
            {
               key: 'strategyModule',
               path: '/strategy-module/:activeTab',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <StrategyModuleIndex />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'strategyModule',
               path: '/strategy-module/strategies/detail/:strategyId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <StrategyDetails />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'strategyModule',
               path: '/strategy-module/targets/detail/:targetId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <StrategyTargetDetails />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'strategyModule',
               path: '/strategy-module/actions/detail/:actionId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <StrategyTargetActionDetails />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'reportEditor',
               path: 'reports/report-editor',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <ReportEditor />
                  </Suspense>
               ),
            },
            {
               path: '/reportingstandards/gri/scoping',
               subject: 'kpiScope',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <KPIScopeSettingsGRI />
                  </Suspense>
               ),
            },

            {
               path: '/reportingstandards/esrs/scoping',
               subject: 'kpiScope',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <KPIScopeSettingsESRS />
                     </Suspense>
                  </Container>
               ),
            },

            {
               path: '/reportingstandards/esrs_set2/scoping',
               subject: 'kpiScope',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <KPIScopeSettingsESRS />
                     </Suspense>
                  </Container>
               ),
            },

            {
               path: '/reportingstandards/:reportingStandard/scoping',
               subject: 'kpiScope',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <KPIScopeSettingsGeneric />
                  </Suspense>
               ),
            },

            { path: '', element: <Navigate replace to="home" /> },
            { path: 'dashboard', element: <Navigate replace to={determineDashboardEntryPath()} /> },
            {
               key: 'dashboard',
               path: 'dashboard/:activeTab',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Overview />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'home',
               path: 'home',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Home />
                     </Suspense>
                  </Container>
               ),
            },

            ...SupplyChain,

            { path: '/climaterisk', element: <Navigate replace to="/climaterisk/analyses" /> },

            {
               key: 'climateRisk',
               path: 'climaterisk/analyses',
               element: (
                  <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ClimateRiskAnalyses />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'climateRisk',
               path: 'climaterisk/analyses/:analysisId',
               element: (
                  <Container maxWidth={false}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ClimateRiskAnalysis />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'climateRisk',
               path: 'climaterisk/actions',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ClimateRiskActions />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'climateRisk',
               path: 'climaterisk/risks',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ClimateRisks />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'climateRisk',
               path: 'climaterisk/segments',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ClimateRiskSegments />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'kpis',
               path: 'kpi/:kpiAreaSlug',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <KPIDetailPage />
                  </Suspense>
               ),
            },

            {
               key: 'kpis',
               path: 'kpi/:kpiAreaSlug/:kpiSlug',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <KPIDetailPage />
                  </Suspense>
               ),
            },

            {
               key: 'kpis',
               path: 'kpi/:kpiAreaSlug/:kpiSlug/:kpiSubIdOrSlug',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <KPIDetailPage />
                  </Suspense>
               ),
            },

            {
               key: 'kpis',
               path: 'kpi/:kpiAreaSlug/:kpiSlug/:kpiSubIdOrSlug/:kpiContentIdOrSlug',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <KPIDetailPage />
                  </Suspense>
               ),
            },

            {
               key: 'kpis',
               path: 'kpi/:kpiAreaSlug/:kpiSlug/:kpiSubIdOrSlug/:kpiContentIdOrSlug/:kpiFieldIdOrSlug',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <KPIDetailPage />
                  </Suspense>
               ),
            },

            {
               key: 'kpi',
               path: 'kpi',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <KPIDetailPage />
                  </Suspense>
               ),
            },

            { path: '/reports', element: <ReportsRedirect /> },

            {
               key: 'reports',
               path: 'reports/documents',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Reports />
                     </Suspense>
                  </Container>
               ),
            },

            { path: '/reports/document-hub', element: <DocumentHubRedirect /> },

            {
               key: 'document-hub',
               path: 'reports/document-hub/:activeTab',
               element: (
                  <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <DocumentHub />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'report',
               path: 'reports/documents/:reportId/documents',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Reports />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'report',
               path: 'reports/documents/:reportId/documents/:documentId',
               element: (
                  <Container maxWidth={false} disableGutters sx={{ paddingY: 0 }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ReportEditorPage />
                     </Suspense>
                  </Container>
               ),
            },

            { path: '/emissions', element: <Navigate replace to="/emissions/ghg/footprints" /> },
            { path: '/emissions/ghg', element: <Navigate replace to="/emissions/ghg/footprints" /> },
            {
               key: 'emissions',
               path: 'emissions/ghg/footprints',
               element: (
                  <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <EmissionFootprints />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/livetemplates',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <LiveTemplates />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/livetemplates/:liveTemplateId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <LiveTemplateDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/livetemplates/:liveTemplateId/:activeSubSlug',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <LiveTemplateDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/results',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <EmissionResults />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/reduction-plan',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ReductionPlan />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/compensation',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Compensation />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'product-hub',
               path: 'product-hub',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ProductHub />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'eudr',
               path: 'eudr',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Eudr />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'eudr',
               path: 'eudr/products/:productId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <EudrProductDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'ppwr',
               path: 'ppwr',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Ppwr />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'ppwr',
               path: 'ppwr/packagings/:packagingId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <PpwrPackagingDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'ppwr',
               path: 'ppwr/risks/:riskId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <PpwrRiskDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'ppwr',
               path: 'ppwr/measures/:measureId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <PpwrMeasureDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/reduction-plan/:reductionPlanId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ReductionPlanDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/footprints/:footprintId/:isLiveTemplateFootprint',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <EmissionDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/footprints/:footprintId/:isLiveTemplateFootprint/:activeSubSlug',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <EmissionDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/footprints/:footprintId/:isLiveTemplateFootprint/:activeSubSlug/:fpCalcSlugOrId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <EmissionDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'emissions',
               path: 'emissions/ghg/footprints/:footprintId/:isLiveTemplateFootprint/:activeSubSlug/:fpCalcSlugOrId/:fpCalcFieldSlugOrId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <EmissionDetail />
                     </Suspense>
                  </Container>
               ),
            },

            { path: '/taxonomy', element: <TaxonomyRedirect /> },

            {
               path: '/taxonomy/:activeTab',
               element: (
                  <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Taxonomy />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'taxonomyDetail',
               path: 'taxonomy/businessactivities/:businessActivityId',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <BusinessActivity />
                  </Suspense>
               ),
            },

            {
               key: 'taxonomyDetailWithObjective',
               path: 'taxonomy/businessactivities/:businessActivityId/:taxonomyObjectiveIdOrSlug',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <BusinessActivity />
                  </Suspense>
               ),
            },

            {
               key: 'tasks',
               path: 'tasks',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Tasks />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'tasks-overview',
               path: 'tasks/overview',
               element: (
                  <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Tasks />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'tasksdetail',
               path: 'tasks/:taskId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <TasksDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'actions',
               path: 'actions',
               element: (
                  <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Actions />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'actiondetail',
               path: 'actions/:taskId',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ActionDetail />
                     </Suspense>
                  </Container>
               ),
            },

            {
               key: 'user',
               path: 'users',
               children: [
                  {
                     path: ':username',
                     element: (
                        <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                           <Suspense fallback={<FullPageLoadingIndicator />}>
                              <Profile />
                           </Suspense>
                        </Container>
                     ),
                  },
                  {
                     path: ':username/:activeTab',
                     element: (
                        <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                           <Suspense fallback={<FullPageLoadingIndicator />}>
                              <Profile />
                           </Suspense>
                        </Container>
                     ),
                  },
               ],
            },

            { path: '/settings', element: <Navigate replace to="/settings/organisations" /> },
            {
               key: 'settings-currencies',
               path: 'settings/:settingsKey',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <Settings fullPageHeight={fullPageHeight} />
                  </Suspense>
               ),
            },

            {
               key: 'settings-currencies',
               path: 'settings/:categoryKey/:settingsKey',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <Settings fullPageHeight={fullPageHeight} />
                  </Suspense>
               ),
            },

            {
               key: 'export-subpages',
               path: 'export/key-figures',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <ReportSubpages key="key-figures" />
                  </Suspense>
               ),
            },
            {
               key: 'export-subpages',
               path: 'export/emissions',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <ReportSubpages key="emissions" />
                  </Suspense>
               ),
            },

            { path: '/status', element: <Navigate replace to={determineStatusEntryPath()} /> },
            {
               key: 'status',
               path: 'status',
               children: [
                  {
                     key: 'keyFiguresStatus',
                     path: 'key-figures',
                     element: (
                        <Suspense fallback={<FullPageLoadingIndicator />}>
                           <KeyFiguresViewRef />
                        </Suspense>
                     ),
                  },
                  {
                     key: 'emissionsStatus',
                     path: 'emissions',
                     element: (
                        <Suspense fallback={<FullPageLoadingIndicator />}>
                           <EmissionsViewRef />
                        </Suspense>
                     ),
                  },
               ],
            },

            {
               path: '/403',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <NotAllowed />
                  </Suspense>
               ),
            },

            {
               path: '/404',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <NotFound />
                  </Suspense>
               ),
            },
            {
               path: '*',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <NotFound />
                  </Suspense>
               ),
            },
            {
               path: '/export',
               element: <Navigate replace to="/export/jobs" />,
            },
            {
               path: '/export/jobs',
               element: (
                  <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Export />
                     </Suspense>
                  </Container>
               ),
            },
            {
               path: '/import',
               element: <Navigate replace to="/import/import-data" />,
            },
            {
               path: '/import/import-data',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ImportDataPage />
                     </Suspense>
                  </Container>
               ),
            },
            {
               path: '/import/jobs',
               element: (
                  <Container maxWidth="xl" sx={{ height: fullPageHeight, display: 'flex', flexDirection: 'column' }}>
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ImportJobsPage />
                     </Suspense>
                  </Container>
               ),
            },
            {
               path: '/import/definitions',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <ImportDefinitionPage />
                     </Suspense>
                  </Container>
               ),
            },
            { path: '/dma', element: <Navigate replace to="/dma/overview" /> },
            {
               path: '/dma/:activeTab',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <DMAPage />
                     </Suspense>
                  </Container>
               ),
            },
            {
               key: 'approval-center',
               path: '/approval-center',
               element: (
                  <Suspense fallback={<FullPageLoadingIndicator />}>
                     <RequireFeatureAccess featureName="ApprovalCenter" canAccess={canAccessApprovalCenter}>
                        <Container maxWidth="xl">
                           <ApprovalCenter />
                        </Container>
                     </RequireFeatureAccess>
                  </Suspense>
               ),
            },
            { path: '/closing-hub', element: <Navigate replace to="/closing-hub/overview" /> },
            {
               key: 'closing-hub',
               path: '/closing-hub/:activeTab',
               element: (
                  <Container maxWidth="xl">
                     <Suspense fallback={<FullPageLoadingIndicator />}>
                        <Hub />
                     </Suspense>
                  </Container>
               ),
            },
         ],
      },
   ];
}
